// File System Access calls for the link popup — ported from
// giraffe-excel-poc's src/sync/fsAccess.ts, which proved this live.
//
// Only runs inside the popup (a real top-level browsing context): Chrome
// blocks `showOpenFilePicker`/`showDirectoryPicker`, and every file write
// after them, inside a cross-origin iframe — regardless of how a handle was
// obtained — unless the embedding page's own `<iframe>` delegates that
// permission, which gTables has no control over inside Giraffe.
//
// The File System Access API exposes no way to get a file handle's parent
// directory (a privacy-motivated platform restriction) — so writing the
// linked JSON into the same folder as the `.xlsx` still needs a real
// directory handle. `startIn: fileHandle` opens the folder dialog already
// inside the right folder, so confirming it is one click, not real
// navigation.

import { idbDelete, idbGet, idbSet } from './idb';

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

const RW: FileSystemHandlePermissionDescriptor = { mode: 'readwrite' };
const LINK_TARGET_KEY = 'linkTarget';

const EXCEL_FILE_TYPES: FilePickerAcceptType[] = [
  {
    description: 'Excel Workbook',
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
  },
];

export interface LinkTarget {
  folderHandle: FileSystemDirectoryHandle;
  /** The linked `.xlsx`'s own name — the JSON is written alongside it, under the same base name. */
  workbookFileName: string;
}

export interface SupportCheck {
  supported: boolean;
  reason?: string;
}

export function checkBrowserSupport(): SupportCheck {
  if (typeof window === 'undefined' || !('showDirectoryPicker' in window)) {
    return {
      supported: false,
      reason: 'This browser does not support the File System Access API. Use a recent desktop Chrome, Edge or another Chromium-based browser.',
    };
  }
  if (!('indexedDB' in window)) {
    return { supported: false, reason: 'This browser does not support IndexedDB, needed to remember the linked folder.' };
  }
  return { supported: true };
}

/** Pick the already-downloaded linked `.xlsx`, then confirm write access to its folder. */
export async function pickLinkedWorkbook(): Promise<LinkTarget> {
  const [fileHandle] = await window.showOpenFilePicker({ types: EXCEL_FILE_TYPES });
  const folderHandle = await window.showDirectoryPicker({ mode: 'readwrite', startIn: fileHandle });
  const target: LinkTarget = { folderHandle, workbookFileName: fileHandle.name };
  await idbSet(LINK_TARGET_KEY, target);
  return target;
}

export async function restoreLinkTarget(): Promise<LinkTarget | null> {
  return idbGet<LinkTarget>(LINK_TARGET_KEY);
}

export async function clearLinkTarget(): Promise<void> {
  await idbDelete(LINK_TARGET_KEY);
}

export async function queryFolderPermission(handle: FileSystemDirectoryHandle): Promise<PermissionState> {
  try {
    return await handle.queryPermission(RW);
  } catch {
    return 'unknown';
  }
}

/** Must be called from a user gesture (e.g. a button click handler). */
export async function requestFolderPermission(handle: FileSystemDirectoryHandle): Promise<PermissionState> {
  return handle.requestPermission(RW);
}

export async function writeTextFile(folder: FileSystemDirectoryHandle, fileName: string, content: string): Promise<void> {
  const fileHandle = await folder.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}

/** The linked `.xlsx`'s current bytes, for the on-save diff against what gTables last exported. */
export async function readWorkbookBytes(folder: FileSystemDirectoryHandle, fileName: string): Promise<ArrayBuffer> {
  const fileHandle = await folder.getFileHandle(fileName);
  const file = await fileHandle.getFile();
  return file.arrayBuffer();
}
